// „Доставка" — ОПЕРАЦИЯТА: начален екран вместо таблица (таблицата е в
// справката „Доставки"). Показва незавършената ми доставка и тези на
// колегите (черновите от сървъра; заета → „Поеми редакцията"), стоковите без
// фактура (по доставчик: „Въведи фактура" · „Приключи без фактура") и
// въведените днес. „+ Нова доставка" при незавършена пита:
// продължи я или я изтрий.
import { useEffect } from "react";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../hooks/useViewport";
import { useDeliveries } from "../../../hooks/useInventory";
import {
  usePayableDeliveries,
  useUninvoicedNotes,
} from "../../../hooks/useInventoryDeliveries";
import type { Delivery } from "../../../api/inventory";
import { PageHeader } from "../components/list/PageHeader";
import { PRIMARY_BUTTON_CLASS, SECONDARY_BUTTON_CLASS } from "../components/list/buttonStyles";
import { InventoryTheme } from "../components/InventoryTheme";
import { useActionToast } from "../components/useActionToast";
import { DraftHomeSection } from "../components/drafts/DraftHomeSection";
import { DraftHomeModals } from "../components/drafts/DraftHomeModals";
import { DRAFT_TEXTS } from "../components/drafts/draftTexts";
import { useDeliveryDocument } from "./components/useDeliveryDocument";
import { TodayDeliveriesSection, UninvoicedNotesSection } from "./components/home/DeliveryHomeSections";
import { useDeliveryHomeDrafts } from "./components/home/useDeliveryHomeDrafts";
import { DeliveryTypeBadge } from "./components/DeliveryBadges";

export const DeliveryHomePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isNarrow } = useViewport();
  const showToast = useActionToast();
  const deliveryDocument = useDeliveryDocument();
  const drafts = useDeliveryHomeDrafts();
  const { mine } = drafts;

  // ─── Данните на секциите ─────────────────────────────────────────────────
  const { data: notes = [] } = useUninvoicedNotes();
  const { data: payable } = usePayableDeliveries();
  const today = dayjs();
  const { data: todayRaw = [] } = useDeliveries({
    startDate: today.startOf("day").toISOString(),
    endDate: today.endOf("day").toISOString(),
  });
  // „Въведени днес" — по кога е въведена (BE с редизайна), иначе по датата
  const todayDeliveries = [...todayRaw]
    .filter((delivery) => dayjs(delivery.createdAt ?? delivery.date).isSame(today, "day"))
    .sort((a, b) => (b.createdAt ?? b.date).localeCompare(a.createdAt ?? a.date));

  // ─── Нова доставка: при незавършена — питаме (`drafts.ask`) ──────────────
  const startNew = () => drafts.startNew();

  // След запис: state.openDoc → toast с „Виж документа"
  useEffect(() => {
    const state = location.state as { openDoc?: Delivery } | null;
    if (!state?.openDoc) return;
    const saved = state.openDoc;
    navigate(location.pathname, { replace: true, state: null });
    drafts.refreshLocal();
    showToast({
      text: t("inventoryPage.deliverySavedToast", { number: saved.invoiceNumber || saved.id }),
      actionLabel: t("inventoryPage.postingViewDocument"),
      onAction: () => deliveryDocument.open(saved, { editable: false }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const unpaidCount = payable?.length ?? 0;
  const subtitle = [
    mine ? t("inventoryPage.homeHasDraft") : null,
    payable ? t("inventoryPage.homeUnpaidCount", { count: unpaidCount }) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <InventoryTheme>
      <div className="flex h-full flex-col bg-canvas text-ink">
        <PageHeader
          title={t("inventoryPage.delivery")}
          subtitle={subtitle || undefined}
          right={
            <>
              <button
                type="button"
                onClick={() => navigate("/inventory/deliveries")}
                className={SECONDARY_BUTTON_CLASS}
              >
                {t("inventoryPage.homeAllDeliveries")}
              </button>
              <button type="button" onClick={startNew} className={PRIMARY_BUTTON_CLASS}>
                {t("inventoryPage.newDeliveryPlus")}
              </button>
            </>
          }
        />
        <div
          className={`flex min-h-0 flex-1 flex-col gap-[26px] overflow-y-auto ${
            isNarrow ? "px-3 pb-10 pt-3.5" : "px-7 pb-12 pt-5"
          }`}
        >
          <DraftHomeSection
            mine={mine}
            others={drafts.others}
            texts={DRAFT_TEXTS.delivery}
            renderBadge={(documentType) => (
              <DeliveryTypeBadge documentType={documentType === "note" ? "note" : "invoice"} />
            )}
            onNew={startNew}
          />
          <UninvoicedNotesSection
            notes={notes}
            onViewNote={(note) => deliveryDocument.open(note)}
            onCloseNotes={deliveryDocument.closeNotes}
            onInvoice={(supplierId, group) =>
              navigate("/inventory/delivery/new", {
                state: { invoiceForNotes: { supplierId, noteIds: group.map((note) => note.id) } },
              })
            }
          />
          <TodayDeliveriesSection
            deliveries={todayDeliveries}
            onView={(delivery) => deliveryDocument.open(delivery)}
          />
        </div>

        <DraftHomeModals drafts={drafts} texts={DRAFT_TEXTS.delivery} />
        {deliveryDocument.modals}
      </div>
    </InventoryTheme>
  );
};

export default DeliveryHomePage;
